import type { PendingPosSale, PosCatalogCacheRow } from './types'
import { POS_IDB_NAME, POS_IDB_VERSION } from './constants'

const STORE_PENDING = 'pendingSales'
const STORE_CATALOG = 'catalogCache'

function reqToPromise<T>(req: IDBRequest): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result as T)
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'))
  })
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'))
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'))
  })
}

let dbPromise: Promise<IDBDatabase> | null = null

export function openPosDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(POS_IDB_NAME, POS_IDB_VERSION)
      req.onerror = () => reject(req.error ?? new Error('Failed to open IndexedDB'))
      req.onsuccess = () => resolve(req.result)
      req.onupgradeneeded = () => {
        const db = req.result
        if (!db.objectStoreNames.contains(STORE_PENDING)) {
          db.createObjectStore(STORE_PENDING, { keyPath: 'localId' })
        }
        if (!db.objectStoreNames.contains(STORE_CATALOG)) {
          db.createObjectStore(STORE_CATALOG, { keyPath: 'storeId' })
        }
      }
    })
  }
  return dbPromise
}

export async function putPendingSale(row: PendingPosSale): Promise<void> {
  const db = await openPosDb()
  const tx = db.transaction(STORE_PENDING, 'readwrite')
  tx.objectStore(STORE_PENDING).put(row)
  await txDone(tx)
}

export async function deletePendingSale(localId: string): Promise<void> {
  const db = await openPosDb()
  const tx = db.transaction(STORE_PENDING, 'readwrite')
  tx.objectStore(STORE_PENDING).delete(localId)
  await txDone(tx)
}

export async function listPendingSalesForStore(storeId: number): Promise<PendingPosSale[]> {
  const db = await openPosDb()
  const tx = db.transaction(STORE_PENDING, 'readonly')
  const store = tx.objectStore(STORE_PENDING)
  const all = await reqToPromise<PendingPosSale[]>(store.getAll())
  await txDone(tx)
  return all.filter((r) => r.storeId === storeId && r.status !== 'synced')
}

export async function getAllPendingSales(): Promise<PendingPosSale[]> {
  const db = await openPosDb()
  const tx = db.transaction(STORE_PENDING, 'readonly')
  const store = tx.objectStore(STORE_PENDING)
  const all = await reqToPromise<PendingPosSale[]>(store.getAll())
  await txDone(tx)
  return all.filter((r) => r.status !== 'synced')
}

export async function putCatalogCache(row: PosCatalogCacheRow): Promise<void> {
  const db = await openPosDb()
  const tx = db.transaction(STORE_CATALOG, 'readwrite')
  tx.objectStore(STORE_CATALOG).put(row)
  await txDone(tx)
}

export async function getCatalogCache(storeId: number): Promise<PosCatalogCacheRow | null> {
  const db = await openPosDb()
  const tx = db.transaction(STORE_CATALOG, 'readonly')
  const row = await reqToPromise<PosCatalogCacheRow | undefined>(
    tx.objectStore(STORE_CATALOG).get(storeId),
  )
  await txDone(tx)
  return row ?? null
}
